import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {AdminApi} from "../../api/api";
import {TemplateItem, TemplateItemOptions} from "../../api/values/template-item";
import {TemplateItemUpsert} from "../../api/values/template-item-upsert";
import {ColumnDef} from "../../components/atoms/tables/table";
import {UploadImageResult} from "../../components/molecules/template-detail-image-upload-input/template-detail-upload-image";
import {
    OptionField,
    SelectedOptionsValue,
} from "../../components/organisms/drawers/template-option-drawer";
import {PageStoreConstructorOptions} from "../../utils/page-store";
import {PageRouter} from "../../utils/route-util";
import {validateLength} from "../../utils/validation/functions/length";
import {ValidationResultItem} from "../../utils/validation/validation-result-item";
import {Stores} from "..";
import {DataType} from "./database-form-base";

export type TableRecord = {
    id: string;
    slug: string;
    label: string;
    type: DataType;
    deleted: boolean;
};

export abstract class DatabaseRecordBasePageStore {
    /**
     * Observables
     */
    @observable public mediaId = "";
    @observable public templateId = "";
    @observable public templateColumnList: TableRecord[] = [];
    @observable public templateName = "";
    @observable public templateNote = "";
    @observable public isDrawerOpen = false;
    @observable public selectedId: string | null = null;
    @observable public values: Record<string, string> = {};
    @observable public selectedOptions: Record<string, SelectedOptionsValue> = {};
    @observable public templateDataLabel = "";
    @observable public templateDataLabelError = "";

    /**
     * Static Defaults
     */
    public static readonly DEFAULT_COLUMN_DEFS: ColumnDef[] = [
        {label: "項目名", width: 150, required: false},
        {label: "データの種類", width: 126, required: false},
        {label: "値", width: 854, required: false},
        {label: "", width: 50, required: false},
    ];
    public get DEFAULT_COLUMN_DEFS(): ColumnDef[] {
        return (this.constructor as typeof DatabaseRecordBasePageStore).DEFAULT_COLUMN_DEFS;
    }

    protected adminApi: AdminApi;
    protected stores: Stores;
    protected pageRouter: PageRouter;

    /**
     * Constructor
     */
    constructor(protected options: PageStoreConstructorOptions) {
        makeObservable(this);
        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;
    }

    /**
     *
     *   @return {TableRecord}
     */
    @computed
    public get selectedRow(): TableRecord | null {
        return this.templateColumnList.find((r) => r.id === this.selectedId) ?? null;
    }

    @computed
    public get optionFields(): OptionField[] {
        return [
            {key: "label", label: "項目名", renderAs: "text"},
            {
                key: "imageUrl",
                label: "画像のリンクURL",
                placeholder: "https://xxxxxxx.xxx/",
                ariaLabel: "画像リンク",
                renderAs: "input",
            },
            {key: "altText", label: "代替テキスト", placeholder: "代替テキスト", renderAs: "input"},
            {
                key: "sourceTitle",
                label: "出典タイトル",
                placeholder: "出典タイトル",
                renderAs: "input",
            },
            {
                key: "sourceUrl",
                label: "出典のリンクURL",
                placeholder: "https://xxxxxxx.xxx/",
                ariaLabel: "出典リンク",
                renderAs: "input",
            },
        ];
    }

    /**
     *   テンプレート名を設定する
     *   @param {string} newName - 新しいテンプレート名
     */
    @action.bound
    public async setTemplateDataLabel(newName: string) {
        this.templateDataLabel = newName;
        if (!newName.trim()) {
            this.templateDataLabelError = "テンプレート名は必須です。";
            return;
        }
        const errs: ValidationResultItem[] = await Promise.resolve(
            validateLength(1, 50)(newName.trim()),
        );
        runInAction(() => {
            this.templateDataLabelError = errs.length ? errs[0].errorMessage : "";
        });
    }

    /**
     * Whether any field has input / options
     */
    public canSave(): boolean {
        const label = this.templateDataLabel.trim();
        if (label.length === 0) return false;
        if (this.templateDataLabelError) return false;
        return this.hasAnyFieldContent();
    }

    /**
     * Whether any record field or its options currently has user input.
     */
    protected hasAnyFieldContent(): boolean {
        return this.templateColumnList.some((r) => {
            const val = (this.values[r.id] ?? "").trim();
            if (val !== "") return true;
            const opts = this.selectedOptions[r.id];
            if (!opts) return false;
            return (
                opts.imageUrl.trim() !== "" ||
                opts.altText.trim() !== "" ||
                opts.sourceTitle.trim() !== "" ||
                opts.sourceUrl.trim() !== ""
            );
        });
    }

    /**
     *  テンプレートを取得する
     */
    public async loadTemplate() {
        try {
            const res = await this.adminApi.getTemplate(
                {mediaId: this.mediaId},
                {templateId: this.templateId},
            );
            runInAction(() => {
                this.templateName = res.title;
                this.templateNote = res.note;
                this.templateColumnList = res.templateColumnList
                    .filter((c) => !c.deleted)
                    .map((c) => ({
                        id: c.id,
                        slug: c.slug,
                        label: c.label,
                        type: c.type,
                        deleted: c.deleted,
                    }));
            });
        } catch (e) {
            throw new Error("Error loading template: " + e);
        }
    }

    /**
     *  Dtoを構築する
     *  @return {TemplateItemUpsert} 構築されたDTO
     *  @protected
     */
    protected buildUpsertDto(): TemplateItemUpsert {
        return new TemplateItemUpsert({
            itemLabel: this.templateDataLabel === "" ? undefined : this.templateDataLabel,
            itemList: this.templateColumnList.map((r) => {
                return new TemplateItem({
                    columnId: r.id,
                    slug: r.slug,
                    type: r.type,
                    value: this.values[r.id] || "",
                    deleted: r.deleted,
                    options: new TemplateItemOptions({
                        link_url: this.selectedOptions[r.id]?.imageUrl || null,
                        alt_text: this.selectedOptions[r.id]?.altText || null,
                        source_text: this.selectedOptions[r.id]?.sourceTitle || null,
                        source_url: this.selectedOptions[r.id]?.sourceUrl || null,
                    }),
                });
            }),
        });
    }

    /**
     * 行を追加する
     * @return {void}
     */
    @action.bound
    public setValue(rowId: string, value: string) {
        this.values[rowId] = value;
    }

    /**
     *
     * ドロワーを開き、選択されたIDのオプションを表示する
     * @param {string} id - 選択された行のID
     * @return {void}
     */

    @action.bound
    public openOptions(id: string): void {
        this.selectedId = id;
        this.isDrawerOpen = true;
        if (!this.selectedOptions[id]) {
            this.selectedOptions[id] = {imageUrl: "", altText: "", sourceTitle: "", sourceUrl: ""};
        }
    }

    /**
     * ドロワーを閉じる
     * @return {void}
     */
    @action.bound
    public closeOptions(): void {
        this.isDrawerOpen = false;
        this.selectedId = null;
    }

    /**
     * ドロワーのオプションを更新する
     * @return {void}
     */
    @action.bound
    public setOption(field: keyof SelectedOptionsValue, value: string): void {
        if (!this.selectedId) return;
        this.selectedOptions[this.selectedId] = {
            ...this.selectedOptions[this.selectedId],
            [field]: value,
        };
    }

    /**
     * 画像がアップロードされたときの処理
     * @param {UploadImageResult}
     * result - アップロード結果
     * @return {void}
     */
    @action.bound
    public onImageUploaded(result: UploadImageResult): void {
        if (result.status === "success") {
            this.values[result.fieldId] = result.url;
        }
    }

    /**
     * メディアIDを設定する
     * @param {string} mediaId - メディアID
     */
    @action
    public setMediaId(mediaId: string) {
        this.mediaId = mediaId;
    }

    /**
     * テンプレートIDを設定する
     * @param {string} templateId - テンプレートID
     */
    @action
    public setTemplateId(templateId: string) {
        this.templateId = templateId;
    }
}
